import React, { useState } from 'react';
import { LoadingPage } from 'ensightful-koala-ui';
import moment from 'moment';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import { useTask } from 'contexts/task';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import groupService from 'services/group';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    Button,
    Card,
    Icon,
    IconButton,
    ProgressBar,
    ResourceChip,
    Stack,
    Tabs,
    Tag,
    TaskCard,
    TaskTile,
    Text,
} from 'shell/components';
import { downloadFileFromURL, getAnalyticsStats, getS3FileURL, getUserPreferredFullName } from 'utils/helper';

const renderDate = (dueDate) => {
    const todayString = moment(new Date()).format('D M YYYY');

    if (moment(dueDate).format('D M YYYY') === todayString) {
        return <Text variant={'p3'}>Today</Text>;
    }

    if (new Date(dueDate) > new Date()) {
        const futureDate =
            moment(dueDate).subtract(1, 'day').format('D M YYYY') === todayString
                ? 'Tomorrow'
                : moment(dueDate).format('MMM D');
        return <Text variant={'p3'}>{futureDate}</Text>;
    }

    const overdueDate =
        moment(dueDate).add(1, 'day').format('D M YYYY') === todayString
            ? 'Yesterday'
            : moment(dueDate).format('MMMM D');

    return (
        <Stack alignItems={'center'} direction={'row'} spacing={2}>
            <CustomColorIcon kind={'error'} />
            <CustomText color={'error'} variant={'p3'}>
                Overdue - {overdueDate}
            </CustomText>
        </Stack>
    );
};

const ChannelDetailsSection = ({
    activeGroupId,
    channels,
    setChannels,
    directs,
    activeChatId,
    setActiveChatId,
    files,
    setEditChannelDialog,
    setManageMembersDialog,
    setAlertDialog,
    setChannelDetailsOpen,
}) => {
    const { auth } = useAuth();
    const { taskList, subtaskList, getUserCollaboratorTasks, getUserCollaboratorSubtasks } = useTask();
    const { accountUsers } = useUser2();
    const { device } = useViewport();

    const [tab, setTab] = useState('mainTask');

    const activeChat = [...channels, ...directs].find(({ id }) => id === activeChatId);

    const renderChannelDetails = () => {
        const getChannelMembers = () => {
            if (!channels) return [];
            if (!activeChat) return [];
            return activeChat.members
                .filter((id) => Boolean(id))
                .map((id) => {
                    const accountUser = accountUsers.find(({ owner_account_id }) => owner_account_id === id);
                    return accountUser;
                });
        };

        const channelMembers = getChannelMembers();

        return (
            <Accordion defaultExpanded>
                <AccordionSummary>
                    <Text variant={'p1'}>Channel Details</Text>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={4}>
                        <Stack>
                            <Stack
                                sx={{ minHeight: 24 }}
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={'space-between'}
                                spacing={2}
                            >
                                <Text variant={'p2'} weight={'semibold'}>
                                    #{activeChat.name}
                                </Text>
                                {activeChat.name !== 'general' && (
                                    <IconButton
                                        aria-label={'show channel settings'}
                                        kind={'edit'}
                                        size={'small'}
                                        variant={'square'}
                                        onClick={() => setEditChannelDialog({ open: true })}
                                    />
                                )}
                            </Stack>
                            <Text color={'light'} variant={'p2'}>
                                {activeChat.description}
                            </Text>
                        </Stack>
                        <Stack spacing={2}>
                            <Text variant={'p2'}>Members ({channelMembers.length})</Text>
                            {channelMembers.map((accountUser) => (
                                <Stack
                                    key={accountUser.owner_account_id}
                                    alignItems={'center'}
                                    direction={'row'}
                                    spacing={2}
                                >
                                    <Avatar size={'small'} user={accountUser} />
                                    <Text variant={'p2'}>{getUserPreferredFullName(accountUser)}</Text>
                                </Stack>
                            ))}
                            {activeChat.name !== 'general' && (
                                <Button
                                    data-cy={'channel-add-members-button'}
                                    sx={{ width: 'fit-content' }}
                                    startIcon={<Icon kind={'add'} />}
                                    variant={'tertiary'}
                                    onClick={() => setManageMembersDialog({ open: true })}
                                >
                                    Add Members
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderChannelFiles = () => {
        const handleSelect = ({ key }, file) => {
            switch (key) {
                case 'download':
                    downloadFileFromURL(getS3FileURL(file.data), file.data.name);
                    break;
                default:
                    break;
            }
        };

        const renderFileList = () => {
            if (!files) return <LoadingPage style={{ height: '10vh' }} />;
            return files.length ? (
                <Stack spacing={2}>
                    {files.map((file) => (
                        <ResourceChip
                            key={file.id}
                            options={[{ key: 'download', value: 'Download' }]}
                            resource={file}
                            size={'medium'}
                            type={'file'}
                            onSelect={handleSelect}
                        />
                    ))}
                </Stack>
            ) : (
                <Text color={'light'} variant={'p2'}>
                    No Images or Files
                </Text>
            );
        };

        return (
            <Accordion>
                <AccordionSummary>
                    <Text variant={'p1'}>Images and Files</Text>
                </AccordionSummary>
                <AccordionDetails>{renderFileList()}</AccordionDetails>
            </Accordion>
        );
    };

    const renderChannelActions = () => {
        if (activeChat.name === 'general') return null;

        const leaveChannel = async () => {
            setChannels((current) =>
                current.map((channel) => {
                    if (channel.id !== activeChatId) return channel;
                    return { ...channel, members: channel.members.filter((id) => id !== auth.owner_account_id) };
                })
            );
            setActiveChatId(channels.find(({ name }) => name === 'general').id);
            const result = await groupService.updateChannel(activeGroupId, {
                id: activeChat.id,
                members: activeChat.members.filter((id) => id !== auth.owner_account_id),
            });
            if (!result.members.length) {
                await groupService.deleteChannel(activeGroupId, { id: activeChat.id });
            }
            setAlertDialog((current) => ({ ...current, open: false }));
        };

        return (
            <Button
                data-cy={'leave-channel-button'}
                sx={{ alignSelf: 'flex-start', mt: 4 }}
                color={'destructive'}
                variant={'borderless'}
                onClick={() =>
                    setAlertDialog((current) => ({
                        ...current,
                        open: true,
                        message: 'Leave Channel',
                        description: 'Are you sure you want to leave this channel?',
                        variant: 'destructive',
                        actionButtonText: 'Leave',
                        cancelButtonText: 'Cancel',
                        onAction: leaveChannel,
                    }))
                }
            >
                Leave Channel
            </Button>
        );
    };

    const renderDirectDetails = () => {
        const correspondentId = activeChat.members.find((id) => id !== auth.owner_account_id);
        const correspondent = accountUsers.find(({ owner_account_id }) => owner_account_id === correspondentId);
        const { completedPoints, totalPoints, percentage } = getAnalyticsStats(
            [...Object.values(taskList.all), ...Object.values(subtaskList)],
            correspondentId
        );

        return (
            <Stack alignItems={'center'} spacing={2}>
                <Avatar size={'x-large'} user={correspondent} />
                {correspondent.role === 'instructor' && <Tag type={'role'} value={'instructor'} />}
                <Text variant={'p1'}>{getUserPreferredFullName(correspondent)}</Text>
                <ProgressBar aria-label={`task progress is at ${percentage} percent`} value={percentage} />
                <Stack sx={{ width: '100%' }} direction={'row'} justifyContent={'space-between'}>
                    <Text color={'light'} noWrap variant={'p3'}>
                        {completedPoints}/{totalPoints} Task Points
                    </Text>
                    <Text color={'light'} noWrap variant={'p3'}>
                        {Math.round(percentage)}%
                    </Text>
                </Stack>
            </Stack>
        );
    };

    const renderTasks = () => {
        const correspondentId = activeChat.members.find((id) => id !== auth.owner_account_id);
        const correspondent = accountUsers.find(({ owner_account_id }) => owner_account_id === correspondentId);
        const mainTasks = getUserCollaboratorTasks(correspondentId);
        const subtasks = getUserCollaboratorSubtasks(correspondentId);
        const options = [
            {
                key: 'mainTask',
                value: 'Main Task',
                content: (
                    <>
                        <Stack spacing={2}>
                            {mainTasks.length ? (
                                mainTasks.map((task) => (
                                    <TaskCard
                                        key={task.id}
                                        getUserById={(ownerAccountId) =>
                                            accountUsers.find(
                                                ({ owner_account_id }) => owner_account_id === ownerAccountId
                                            )
                                        }
                                        task={task}
                                        variant={'simplified'}
                                    />
                                ))
                            ) : (
                                <Text sx={{ mt: 2 }} color={'light'} variant={'p2'}>
                                    No tasks owned.
                                </Text>
                            )}
                        </Stack>
                    </>
                ),
            },
            {
                key: 'subtask',
                value: 'Subtask',
                content: (
                    <>
                        <Stack spacing={2}>
                            {subtasks.length ? (
                                subtasks.map((subtask) => (
                                    <TaskTile
                                        sx={{ cursor: 'default' }}
                                        key={subtask.id}
                                        endComponent={null}
                                        getNameById={() => getUserPreferredFullName(correspondent)}
                                        task={subtask}
                                    />
                                ))
                            ) : (
                                <Text sx={{ mt: 2 }} color={'light'} variant={'p2'}>
                                    No subtasks owned.
                                </Text>
                            )}
                        </Stack>
                    </>
                ),
            },
        ];

        if (correspondent.role !== 'student') return null;

        return (
            <Accordion>
                <AccordionSummary>
                    <Text variant={'p1'}>Owned Tasks</Text>
                </AccordionSummary>
                <AccordionDetails>
                    <Tabs aria-label={'select task tab'} options={options} selected={tab} onChange={setTab} />
                </AccordionDetails>
            </Accordion>
        );
    };

    return (
        <RootBox device={device}>
            {device === 'mobile' && (
                <IconButton
                    data-cy={'channel-detail-back-button'}
                    aria-label={'back'}
                    sx={{ mb: 2 }}
                    kind={'arrowLeft'}
                    size={'medium'}
                    variant={'square'}
                    onClick={() => setChannelDetailsOpen(false)}
                />
            )}
            {activeChat.chat_type === 'channel' ? (
                <Stack spacing={4}>
                    {renderChannelDetails()}
                    {renderChannelFiles()}
                    {renderChannelActions()}
                </Stack>
            ) : (
                <Stack spacing={4}>
                    {renderDirectDetails()}
                    {renderTasks()}
                    {renderChannelFiles()}
                </Stack>
            )}
        </RootBox>
    );
};

const RootBox = styled(Box)(({ device, theme }) => {
    return {
        flexShrink: 0,
        maxHeight: 'calc(100vh - 64px - 20px)',
        padding: theme.spacing(4),
        overflowY: 'auto',
        ...(device === 'mobile' && {
            width: '100%',
        }),
        ...(device === 'tablet' && {
            width: 229,
        }),
        ...(device === 'desktop' && {
            width: 350,
        }),
    };
});

const CustomColorIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        padding: '0.2rem',
        fontSize: '1rem',
        background: theme.palette.ensightful.error[700],
        color: theme.palette.ensightful.shades[0],
    };
});

const CustomText = styled(Text)(() => {
    return {
        textDecoration: 'underline',
    };
});

export default ChannelDetailsSection;
